import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/database.types';

// ─── Project endpoint + anon key ─────────────────────────────────
// Both are public values (URL = project endpoint, anon key = JWT
// meant to ship to browsers). Hardcoded fallbacks make the client
// robust against env-var misconfig in deploy environments — if the
// Cloudflare Pages build sometimes drops VITE_SUPABASE_ANON_KEY,
// every PostgREST call would otherwise fail with "Invalid API key".

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ||
  'https://ngqhgvnpjlunauipdlwy.supabase.co';

// Prefer the new publishable key format (supabase-js v2.100+ expects this);
// fall back to the legacy anon JWT for older builds. Both are public values.
const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  'sb_publishable_1TgDwNNkTFsqZBEsHpHxxw_qLgf31tk';

// ─── Supabase client (singleton) ──────────────────────────────────
// We use the typed client so all queries get autocomplete and type checking
// from our schema (via @/types/database.types).

export const supabase: SupabaseClient<Database> = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce', // More secure for SPAs
    },
    global: {
      headers: {
        'x-application': 'plansync-web',
      },
    },
    realtime: {
      params: {
        eventsPerSecond: 10,
      },
    },
  },
);

// ─── Helper: get current user ID (or null) ────────────────────────

export async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

// ─── Helper: subscribe to auth state ──────────────────────────────

export function onAuthChange(callback: (userId: string | null) => void) {
  return supabase.auth.onAuthStateChange((_event, session) => {
    callback(session?.user?.id ?? null);
  });
}
